import { Field, Form, Formik } from "formik";
import React, { useEffect, useRef, useState } from "react";
import KibabMenu from "../../../../assets/icons/kibab-menu";
import { useDispatch } from "react-redux";
import {
  addItemCategoryAction,
  deleteItemCategoryAction,
  itemCategoryListAction,
  updateItemCategoryAction,
} from "../../../../redux/actions/settings-action";
import errorToast from "../../../helper-components/error-toast";
import DeleteIcon from "../../../../assets/icons/delete-icon";
import successToast from "../../../helper-components/success-toast";
import AddButton from "../../../buttons/button";
import LoadingSpinner from "../../../helper-components/loading-spinner";

const SLOW_FAST_NUMERIC_FIELDS = [
  "item_minimum_qty",
  "item_maximum_qty",
  "item_minimum_days",
  "item_maximum_days",
];

const toNumberOrNull = (value) => {
  if (value === "" || value === null || value === undefined) return null;
  const num = Number(value);
  return Number.isNaN(num) ? null : num;
};

// The inputs keep these as strings while typing — always send numbers.
const withNumericSlowFastFields = (row) => ({
  ...row,
  ...Object.fromEntries(
    SLOW_FAST_NUMERIC_FIELDS.map((field) => [
      field,
      toNumberOrNull(row?.[field]),
    ])
  ),
});

const ItemCategoryTable = ({ searchValue, reload }) => {
  const [disableArrayFields, setDisableArrayFields] = useState(false);
  const [disableNewFields, setDisableNewFields] = useState(false);
  const [selectedDOtIndex, setSelectedDOtIndex] = useState(null);
  const [initialData, setItemCategoryData] = useState([]);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  const handleFieldChange = (index, field, value, setFieldValue) => {
    if (index === "newLine") {
      setDisableArrayFields(true);
      setDisableNewFields(false);
    } else {
      setDisableArrayFields(false);
      setDisableNewFields(true);
    }
    setFieldValue(`${index}.${field}`, value);
  };

  // Shared handler for the 4 new numeric fields (digits only, same
  // validation pattern already used for code/name).
  const handleNumericFieldChange = (
    index,
    field,
    rawValue,
    setFieldValue
  ) => {
    const cleanedValue = rawValue.replace(/[^0-9]/g, "");
    if (rawValue !== cleanedValue) {
      errorToast("Only numbers are allowed");
    }
    handleFieldChange(index, field, cleanedValue, setFieldValue);
  };

  useEffect(() => {
    dispatch(
      itemCategoryListAction({}, onFetchItemTypeSuccess, onFetchItemTypeError)
    );
    setLoading(true);
  }, []);

  useEffect(() => {
    dispatch(
      itemCategoryListAction(
        { searchValue },
        onFetchItemTypeSuccess,
        onFetchItemTypeError
      )
    );
    setLoading(true);
  }, [searchValue, reload]);

  const reloadData = () => {
    dispatch(
      itemCategoryListAction({}, onFetchItemTypeSuccess, onFetchItemTypeError)
    );
    setLoading(true);
  };

  const onFetchItemTypeSuccess = (data) => {
    setItemCategoryData(data?.data?.itemCategoryList);
    setLoading(false);
  };

  const onFetchItemTypeError = (data) => {
    setLoading(false);
    setItemCategoryData([]);
    errorToast(data?.data?.message);
  };

  const initialValues = {
    data: initialData,
    newLine: {
      code: "",
      name: "",
      item_minimum_qty: "",
      item_maximum_qty: "",
      item_minimum_days: "",
      item_maximum_days: "",
    },
  };

  const onSubmit = (values, { setSubmitting }) => {
    const changedObjects = [];
    let newLineObject = null;

    // Collect changed array data
    initialData.forEach((item, index) => {
      if (JSON.stringify(values.data[index]) !== JSON.stringify(item)) {
        changedObjects.push(values.data[index]);
      }
    });

    // Collect new line data if it is altered
    if (
      JSON.stringify(values.newLine) !== JSON.stringify(initialValues.newLine)
    ) {
      newLineObject = values.newLine;
    }

    if (newLineObject !== null) {
      if (newLineObject.code === "" || newLineObject.name === "") {
        errorToast("Please fill all the fields in the new line.");
        setSubmitting(false);
        return;
      }
      dispatch(
        addItemCategoryAction(
          withNumericSlowFastFields(newLineObject),
          onCreateSuccess,
          onCreateError
        )
      );
    }

    if (changedObjects.length > 0) {
      dispatch(
        updateItemCategoryAction(
          { updatingData: changedObjects.map(withNumericSlowFastFields) },
          onUpdateSuccess,
          onUpdateError
        )
      );
    }
    setSubmitting(false);
  };

  const onCreateSuccess = (data) => {
    reloadData();
    setDisableArrayFields(false);
    setDisableNewFields(false);
    successToast(data?.message);
  };
  const onCreateError = (data) => {
    setDisableArrayFields(false);
    setDisableNewFields(false);
    errorToast(data?.data?.message);
  };

  const onUpdateSuccess = (data) => {
    reloadData();
    setDisableArrayFields(false);
    setDisableNewFields(false);
    successToast(data?.message);
  };
  const onUpdateError = (data) => {
    setDisableArrayFields(false);
    setDisableNewFields(false);
    errorToast(data?.data?.message);
  };

  const onClickDelete = (id) => {
    dispatch(deleteItemCategoryAction({ id }, onDeletSuccess, onError));
  };
  const onDeletSuccess = (data) => {
    reloadData();
    setDisableArrayFields(false);
    setDisableNewFields(false);
    successToast(data?.message);
  };

  const onError = (data) => {
    errorToast(data?.data?.message);

    selectedDOtIndex(null);
  };

  const componentRef = useRef(null);

  const handleClickOutside = (event) => {
    if (componentRef.current && !componentRef.current.contains(event.target)) {
      setSelectedDOtIndex(null);
    }
  };

  return (
    <div>
      <Formik
        enableReinitialize={true}
        initialValues={initialValues}
        onSubmit={onSubmit}
      >
        {({ values, setFieldValue, dirty, handleSubmit }) => (
          <Form className="pt-1">
            <div
              onClick={handleClickOutside}
              className="flex flex-col justify-between"
              style={{ height: "75vh" }}
            >
              {loading ? (
                <LoadingSpinner />
              ) : (
                <div className="overflow-auto mb-auto rounded-xl table-container border border-gray-200">
                  <table className=" w-full ">
                    <thead className="sticky top-0 bg-[var(--create-table-header-bg)] z-[10]">
                      <tr className="relative h-[50px]">
                        <th
                          rowSpan={2}
                          className="text-start px-[8px] whitespace-nowrap text-[var(--create-table-header-fg)] text-[length:var(--create-table-header-fs)] font-[var(--create-table-header-fw)]"
                        >
                          CATEGORY CODE
                        </th>
                        <th
                          rowSpan={2}
                          className="text-start px-[8px] whitespace-nowrap text-[var(--create-table-header-fg)] text-[length:var(--create-table-header-fs)] font-[var(--create-table-header-fw)]"
                        >
                          CATEGORY NAME
                        </th>
                        <th
                          colSpan={2}
                          className="w-[180px] text-center px-[8px] whitespace-nowrap text-[var(--create-table-header-fg)] text-[length:var(--create-table-header-fs)] font-[var(--create-table-header-fw)]"
                        >
                          QUANTITY
                        </th>
                        <th
                          colSpan={2}
                          className="w-[180px] text-center px-[8px] whitespace-nowrap text-[var(--create-table-header-fg)] text-[length:var(--create-table-header-fs)] font-[var(--create-table-header-fw)]"
                        >
                          DAYS
                        </th>
                        <th
                          rowSpan={2}
                          className="text-start px-[8px] whitespace-nowrap text-[var(--create-table-header-fg)] text-[length:var(--create-table-header-fs)] font-[var(--create-table-header-fw)]"
                        >
                          ACTIONS
                        </th>
                      </tr>
                      <tr className="relative h-[34px]">
                        <th className="w-[90px] text-center px-[8px] whitespace-nowrap text-[var(--create-table-header-fg)] text-[11px] font-[var(--create-table-header-fw)] opacity-80">
                          MIN
                        </th>
                        <th className="w-[90px] text-center px-[8px] whitespace-nowrap text-[var(--create-table-header-fg)] text-[11px] font-[var(--create-table-header-fw)] opacity-80">
                          MAX
                        </th>
                        <th className="w-[90px] text-center px-[8px] whitespace-nowrap text-[var(--create-table-header-fg)] text-[11px] font-[var(--create-table-header-fw)] opacity-80">
                          MIN
                        </th>
                        <th className="w-[90px] text-center px-[8px] whitespace-nowrap text-[var(--create-table-header-fg)] text-[11px] font-[var(--create-table-header-fw)] opacity-80">
                          MAX
                        </th>
                      </tr>
                    </thead>
                    <tbody style={{ position: "relative" }}>
                      {values.data.map((item, index) => (
                        <tr key={index}>
                          <td
                            className={`tab-cell text-sm fw_400 text-secondary w-3/12`}
                          >
                            <Field
                              name={`data[${index}].code`}
                              placeholder="Code"
                              disabled={disableArrayFields}
                              onChange={(e) => {
                                const rawValue = e.target.value;
                                const cleanedValue = rawValue.replace(
                                  /[^a-zA-Z0-9]/g,
                                  ""
                                );

                                if (rawValue !== cleanedValue) {
                                  // 🚨 Invalid character found
                                  errorToast(
                                    "Only letters and numbers are allowed"
                                  );
                                }
                                handleFieldChange(
                                  `data[${index}]`,
                                  "code",
                                  cleanedValue,
                                  setFieldValue
                                );
                              }}
                              className="border-0 focus:outline-none focus:ring focus:ring-blue-500 rounded px-1 py-2"
                            />
                          </td>
                          <td
                            className={`tab-cell text-sm fw_400 text-secondary`}
                          >
                            <Field
                              name={`data[${index}].name`}
                              placeholder="Name"
                              disabled={disableArrayFields}
                              onChange={(e) => {
                                let value = e.target.value;
                                const originalValue = value;

                                // 1. Remove special characters (only letters, numbers, spaces allowed)
                                value = value.replace(/[^a-zA-Z0-9 ]/g, "");

                                // Show error toast if invalid characters were entered
                                if (originalValue !== value) {
                                  errorToast(
                                    "Only letters, numbers, and spaces are allowed"
                                  );
                                }

                                // 2. Prevent leading space
                                value = value.replace(/^\s+/, "");

                                // 3. Prevent consecutive spaces and show error toast
                                if (value !== value.replace(/\s+/g, " ")) {
                                  errorToast(
                                    "Consecutive spaces are not allowed"
                                  );
                                }
                                value = value.replace(/\s+/g, " ");

                                handleFieldChange(
                                  `data[${index}]`,
                                  "name",
                                  value,
                                  setFieldValue
                                );
                              }}
                              className="border-0 focus:outline-none focus:ring focus:ring-blue-500 rounded py-2"
                            />
                          </td>
                          <td className="tab-cell text-sm fw_400 text-secondary text-center w-[90px]">
                            <Field
                              type="number"
                              name={`data[${index}].item_minimum_qty`}
                              placeholder="Min"
                              disabled={disableArrayFields}
                              onChange={(e) =>
                                handleNumericFieldChange(
                                  `data[${index}]`,
                                  "item_minimum_qty",
                                  e.target.value,
                                  setFieldValue
                                )
                              }
                              className="border-0 focus:outline-none focus:ring focus:ring-blue-500 rounded px-1 py-2 w-16 text-center"
                            />
                          </td>
                          <td className="tab-cell text-sm fw_400 text-secondary text-center w-[90px]">
                            <Field
                              type="number"
                              name={`data[${index}].item_maximum_qty`}
                              placeholder="Max"
                              disabled={disableArrayFields}
                              onChange={(e) =>
                                handleNumericFieldChange(
                                  `data[${index}]`,
                                  "item_maximum_qty",
                                  e.target.value,
                                  setFieldValue
                                )
                              }
                              className="border-0 focus:outline-none focus:ring focus:ring-blue-500 rounded px-1 py-2 w-16 text-center"
                            />
                          </td>
                          <td className="tab-cell text-sm fw_400 text-secondary text-center w-[90px]">
                            <Field
                              type="number"
                              name={`data[${index}].item_minimum_days`}
                              placeholder="Min"
                              disabled={disableArrayFields}
                              onChange={(e) =>
                                handleNumericFieldChange(
                                  `data[${index}]`,
                                  "item_minimum_days",
                                  e.target.value,
                                  setFieldValue
                                )
                              }
                              className="border-0 focus:outline-none focus:ring focus:ring-blue-500 rounded px-1 py-2 w-16 text-center"
                            />
                          </td>
                          <td className="tab-cell text-sm fw_400 text-secondary text-center w-[90px]">
                            <Field
                              type="number"
                              name={`data[${index}].item_maximum_days`}
                              placeholder="Max"
                              disabled={disableArrayFields}
                              onChange={(e) =>
                                handleNumericFieldChange(
                                  `data[${index}]`,
                                  "item_maximum_days",
                                  e.target.value,
                                  setFieldValue
                                )
                              }
                              className="border-0 focus:outline-none focus:ring focus:ring-blue-500 rounded px-1 py-2 w-16 text-center"
                            />
                          </td>
                          <td className="tab-cell text-sm fw_400 text-secondary w-2/12">
                            <div className="flex items-center">
                              <button
                                type="button"
                                onClick={() => onClickDelete(item?.id)}
                              >
                                <DeleteIcon />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      <tr>
                        <td
                          className={`tab-cell text-sm fw_400 text-secondary`}
                        >
                          <Field
                            name="newLine.code"
                            placeholder="Code"
                            disabled={disableNewFields}
                            onChange={(e) => {
                              const rawValue = e.target.value;
                              const cleanedValue = rawValue.replace(
                                /[^a-zA-Z0-9]/g,
                                ""
                              );

                              if (rawValue !== cleanedValue) {
                                // 🚨 Invalid character found
                                errorToast(
                                  "Only letters and numbers are allowed"
                                );
                              }
                              handleFieldChange(
                                `newLine`,
                                "code",
                                cleanedValue,
                                setFieldValue
                              );
                            }}
                            className="border-0 focus:outline-none focus:ring focus:ring-blue-500 rounded py-2"
                            autoFocus={searchValue ? false : true}
                          />
                        </td>
                        <td
                          className={`tab-cell text-sm fw_400 text-secondary`}
                        >
                          <Field
                            name="newLine.name"
                            placeholder="Name"
                            disabled={disableNewFields}
                            onChange={(e) => {
                              let value = e.target.value;
                              const originalValue = value;

                              // 1. Remove special characters (only letters, numbers, spaces allowed)
                              value = value.replace(/[^a-zA-Z0-9 ]/g, "");

                              // Show error toast if invalid characters were entered
                              if (originalValue !== value) {
                                errorToast(
                                  "Only letters, numbers, and spaces are allowed"
                                );
                              }

                              // 2. Prevent leading space
                              value = value.replace(/^\s+/, "");

                              // 3. Prevent consecutive spaces and show error toast
                              if (value !== value.replace(/\s+/g, " ")) {
                                errorToast(
                                  "Consecutive spaces are not allowed"
                                );
                              }
                              value = value.replace(/\s+/g, " ");

                              handleFieldChange(
                                "newLine",
                                "name",
                                value,
                                setFieldValue
                              );
                            }}
                            className="border-0 focus:outline-none focus:ring focus:ring-blue-500 rounded py-2"
                          />
                        </td>
                        <td className="tab-cell text-sm fw_400 text-secondary text-center w-[90px]">
                          <Field
                            type="number"
                            name="newLine.item_minimum_qty"
                            placeholder="Min"
                            disabled={disableNewFields}
                            onChange={(e) =>
                              handleNumericFieldChange(
                                "newLine",
                                "item_minimum_qty",
                                e.target.value,
                                setFieldValue
                              )
                            }
                            className="border-0 focus:outline-none focus:ring focus:ring-blue-500 rounded px-1 py-2 w-16 text-center"
                          />
                        </td>
                        <td className="tab-cell text-sm fw_400 text-secondary text-center w-[90px]">
                          <Field
                            type="number"
                            name="newLine.item_maximum_qty"
                            placeholder="Max"
                            disabled={disableNewFields}
                            onChange={(e) =>
                              handleNumericFieldChange(
                                "newLine",
                                "item_maximum_qty",
                                e.target.value,
                                setFieldValue
                              )
                            }
                            className="border-0 focus:outline-none focus:ring focus:ring-blue-500 rounded px-1 py-2 w-16 text-center"
                          />
                        </td>
                        <td className="tab-cell text-sm fw_400 text-secondary text-center w-[90px]">
                          <Field
                            type="number"
                            name="newLine.item_minimum_days"
                            placeholder="Min"
                            disabled={disableNewFields}
                            onChange={(e) =>
                              handleNumericFieldChange(
                                "newLine",
                                "item_minimum_days",
                                e.target.value,
                                setFieldValue
                              )
                            }
                            className="border-0 focus:outline-none focus:ring focus:ring-blue-500 rounded px-1 py-2 w-16 text-center"
                          />
                        </td>
                        <td className="tab-cell text-sm fw_400 text-secondary text-center w-[90px]">
                          <Field
                            type="number"
                            name="newLine.item_maximum_days"
                            placeholder="Max"
                            disabled={disableNewFields}
                            onChange={(e) =>
                              handleNumericFieldChange(
                                "newLine",
                                "item_maximum_days",
                                e.target.value,
                                setFieldValue
                              )
                            }
                            className="border-0 focus:outline-none focus:ring focus:ring-blue-500 rounded px-1 py-2 w-16 text-center"
                          />
                        </td>
                        <td
                          className={`tab-cell text-sm fw_400 text-secondary`}
                        ></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}

              <div className="mt-auto flex gap-3 items-center justify-end w-full h-[4rem]">
                <AddButton
                  onclkFn={handleSubmit}
                  prefixIcon={false}
                  text="Save"
                  type="submit"
                  className="
    flex items-center px-3 py-2  whitespace-nowrap rounded-md gap-[8px]
    bg-[var(--primary-button-bg-color)] cursor-pointer
    text-[var(--primary-button-fg-color)]
    border border-[var(--primary-button-bg-color)]
    hover:bg-[var(--primary-button-hover-color)]
    text-[length:var(--primary-button-create-fs)]
    font-[var(--primary-button-font-weight)]
    "
                />
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default ItemCategoryTable;